import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import mapboxgl from "mapbox-gl";
import MapView, { Layer, NavigationControl, Popup, Source } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import Sidebar from "@/components/layout/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Activity, AlertTriangle, Clock, CreditCard, MapPin, RefreshCw, ShieldCheck, Truck, Users, Radar, LocateFixed, Layers, CheckCircle2, XCircle, GripHorizontal } from "lucide-react";
import DashboardDateTimeWidget from "@/components/layout/DashboardDateTimeWidget";
import SiteFooter from "@/components/layout/SiteFooter";
import { EMERGENCY_SERVICE_TYPES, EMERGENCY_SEVERITIES, formatEmergencyDistance, formatEmergencyEta, getEmergencyReportStatusBadgeClass, getEmergencyReportStatusLabel, getEmergencyReportTypeLabel, getEmergencyServiceBadgeClass, getEmergencyServiceColor, getEmergencyServiceStatusLabel, getEmergencyServiceTypeLabel, getEmergencySeverityBadgeClass, getEmergencySeverityColor, getEmergencySeverityLabel } from "@/lib/emergency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAP_STYLE = "mapbox://styles/mapbox/navigation-night-v1";

const roleColors = {
  admin: "bg-orange-600/20 text-orange-200 border border-orange-500/40",
  dispatcher: "bg-amber-600/20 text-amber-100 border border-amber-500/40",
  distributor: "bg-yellow-700/20 text-yellow-100 border border-yellow-600/40",
  driver: "bg-orange-500/20 text-orange-100 border border-orange-400/40",
  sales_rep: "bg-cyan-600/20 text-cyan-100 border border-cyan-500/40",
  emergency: "bg-red-600/20 text-red-100 border border-red-500/40",
};

const driverStatusStyles = {
  available: { color: "#22c55e", label: "Available" },
  on_duty: { color: "#3b82f6", label: "On Duty" },
  on_break: { color: "#f59e0b", label: "On Break" },
  off_duty: { color: "#94a3b8", label: "Off Duty" },
};

const statusRingColor = [
  "match",
  ["get", "driver_status"],
  "available",
  "#22c55e",
  "on_duty",
  "#3b82f6",
  "on_break",
  "#f59e0b",
  "off_duty",
  "#94a3b8",
  [
    "match",
    ["get", "point_type"],
    "distribution_center",
    "#60a5fa",
    "factory",
    "#f59e0b",
    "truck",
    "#34d399",
    "#d6d3d1",
  ],
];

const clusterLayer = {
  id: "fleet-clusters",
  type: "circle",
  source: "fleet",
  filter: ["has", "point_count"],
  paint: {
    "circle-color": ["step", ["get", "point_count"], "#ea580c", 10, "#fb923c", 30, "#f97316"],
    "circle-radius": ["step", ["get", "point_count"], 18, 25, 22, 100, 28],
    "circle-opacity": 0.9,
    "circle-stroke-width": 1.3,
    "circle-stroke-color": "#fed7aa",
  },
};

const clusterCountLayer = {
  id: "fleet-cluster-count",
  type: "symbol",
  source: "fleet",
  filter: ["has", "point_count"],
  layout: {
    "text-field": ["to-string", ["get", "point_count"]],
    "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
    "text-size": 12,
  },
  paint: {
    "text-color": "#111827",
  },
};

const pointOuterLayer = {
  id: "fleet-points-outer",
  type: "circle",
  source: "fleet",
  filter: ["!", ["has", "point_count"]],
  paint: {
    "circle-color": "#0b0a08",
    "circle-radius": ["case", ["==", ["get", "point_type"], "truck"], 9, 7],
    "circle-stroke-color": statusRingColor,
    "circle-stroke-width": 2,
  },
};

const pointPulseLayer = {
  id: "fleet-points-pulse",
  type: "circle",
  source: "fleet",
  filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "point_type"], "truck"]],
  paint: {
    "circle-color": statusRingColor,
    "circle-radius": 14,
    "circle-opacity": 0.18,
  },
};

const trafficLayer = {
  id: "traffic-live",
  type: "line",
  source: "traffic",
  "source-layer": "traffic",
  paint: {
    "line-width": ["interpolate", ["linear"], ["zoom"], 5, 0.8, 9, 1.6, 13, 3.2],
    "line-opacity": 0.95,
    "line-color": [
      "match",
      ["get", "congestion"],
      "low",
      "#4ade80",
      "moderate",
      "#facc15",
      "heavy",
      "#f97316",
      "severe",
      "#dc2626",
      "#94a3b8",
    ],
  },
};

const tripRouteGlowLayer = {
  id: "trip-routes-glow",
  type: "line",
  source: "trip-routes",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["coalesce", ["get", "route_glow_color"], "#9a3412"],
    "line-width": ["interpolate", ["linear"], ["zoom"], 4, 5, 8, 8, 12, 12],
    "line-opacity": 0.34,
    "line-blur": 1.1,
  },
};

const tripRouteLineLayer = {
  id: "trip-routes-line",
  type: "line",
  source: "trip-routes",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["coalesce", ["get", "route_color"], "#b45309"],
    "line-width": ["interpolate", ["linear"], ["zoom"], 4, 2.8, 8, 4.6, 12, 6.4],
    "line-opacity": ["case", ["==", ["get", "trip_status"], "paused"], 0.58, 0.98],
  },
};

const tripRouteHitboxLayer = {
  id: "trip-routes-hitbox",
  type: "line",
  source: "trip-routes",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": "#ffffff",
    "line-width": ["interpolate", ["linear"], ["zoom"], 4, 10, 8, 14, 12, 18],
    "line-opacity": 0.01,
  },
};

const destinationOuterLayer = {
  id: "trip-destinations-outer",
  type: "circle",
  source: "trip-destinations",
  paint: {
    "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 5, 8, 7, 12, 9],
    "circle-color": "#0b0a08",
    "circle-stroke-width": 2,
    "circle-stroke-color": ["coalesce", ["get", "route_color"], "#f97316"],
  },
};

const destinationInnerLayer = {
  id: "trip-destinations",
  type: "circle",
  source: "trip-destinations",
  paint: {
    "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 2, 8, 3, 12, 4],
    "circle-color": ["coalesce", ["get", "route_color"], "#f97316"],
    "circle-opacity": 0.95,
  },
};

const destinationLabelLayer = {
  id: "trip-destination-labels",
  type: "symbol",
  source: "trip-destinations",
  minzoom: 5.5,
  layout: {
    "text-field": ["coalesce", ["get", "destination_short"], ["get", "driver_name"], "Destination"],
    "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
    "text-size": 11,
    "text-offset": [0, 1.4],
    "text-anchor": "top",
  },
  paint: {
    "text-color": "#f5f5f4",
    "text-halo-color": "rgba(12, 10, 9, 0.95)",
    "text-halo-width": 1.1,
  },
};

const emergencyServiceLayer = {
  id: "emergency-services",
  type: "circle",
  source: "emergency-services",
  paint: {
    "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 4.5, 8, 6.5, 12, 8.5],
    "circle-color": ["coalesce", ["get", "marker_color"], "#f97316"],
    "circle-stroke-width": 2,
    "circle-stroke-color": ["case", ["==", ["get", "status"], "closed"], "#292524", "#fff7ed"],
    "circle-opacity": ["case", ["==", ["get", "status"], "closed"], 0.48, 0.9],
  },
};

const emergencyReportLayer = {
  id: "emergency-reports",
  type: "circle",
  source: "emergency-reports",
  paint: {
    "circle-radius": [
      "match",
      ["get", "severity"],
      "critical",
      10,
      "urgent",
      9.2,
      "high",
      8.5,
      "medium",
      7,
      "advisory",
      5.2,
      6,
    ],
    "circle-color": ["coalesce", ["get", "marker_color"], "#ef4444"],
    "circle-stroke-width": 2,
    "circle-stroke-color": ["case", ["==", ["get", "status"], "resolved"], "#d6d3d1", "#fff7ed"],
    "circle-opacity": ["case", ["==", ["get", "status"], "resolved"], 0.45, 0.9],
  },
};

const emergencyRouteLineLayer = {
  id: "emergency-routes-line",
  type: "line",
  source: "emergency-routes",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["coalesce", ["get", "route_color"], "#38bdf8"],
    "line-width": ["interpolate", ["linear"], ["zoom"], 4, 2.6, 8, 4.2, 12, 6.2],
    "line-opacity": 0.94,
  },
};

const emergencyRouteHitboxLayer = {
  id: "emergency-routes-hitbox",
  type: "line",
  source: "emergency-routes",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": "#ffffff",
    "line-width": ["interpolate", ["linear"], ["zoom"], 4, 10, 8, 14, 12, 18],
    "line-opacity": 0.01,
  },
};

const metersToLat = (meters) => meters / 111320;
const metersToLng = (meters, lat) => meters / (111320 * Math.cos((lat * Math.PI) / 180) || 1);

const separateOverlappingPoints = (points) => {
  const grouped = new globalThis.Map();
  for (const point of points) {
    const lat = Number(point.latitude);
    const lng = Number(point.longitude);
    const key = `${lat.toFixed(5)}:${lng.toFixed(5)}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(point);
  }

  const adjusted = [];
  for (const group of grouped.values()) {
    if (group.length === 1) {
      adjusted.push({ ...group[0], plot_latitude: Number(group[0].latitude), plot_longitude: Number(group[0].longitude) });
      continue;
    }
    group.forEach((point, idx) => {
      const angle = (idx / group.length) * Math.PI * 2;
      const radiusMeters = 20 + Math.floor(idx / 8) * 12;
      const baseLat = Number(point.latitude);
      const baseLng = Number(point.longitude);
      const latOffset = metersToLat(Math.sin(angle) * radiusMeters);
      const lngOffset = metersToLng(Math.cos(angle) * radiusMeters, baseLat);
      adjusted.push({
        ...point,
        plot_latitude: baseLat + latOffset,
        plot_longitude: baseLng + lngOffset,
      });
    });
  }
  return adjusted;
};

const formatDriveTime = (seconds) => {
  const total = Number(seconds || 0);
  const hrs = Math.floor(total / 3600).toString().padStart(2, "0");
  const mins = Math.floor((total % 3600) / 60).toString().padStart(2, "0");
  const secs = Math.floor(total % 60).toString().padStart(2, "0");
  return `${hrs}:${mins}:${secs}`;
};

const routePalette = ["#b45309", "#92400e", "#a16207", "#9a3412", "#c2410c", "#78350f"];
const routeGlowPalette = ["#fdba74", "#fb923c", "#f59e0b", "#f97316", "#fbbf24", "#ea580c"];

const hashString = (value) => {
  let hash = 0;
  for (const char of String(value || "")) {
    hash = ((hash << 5) - hash + char.charCodeAt(0)) | 0;
  }
  return Math.abs(hash);
};

const getRouteColor = (value) => routePalette[hashString(value) % routePalette.length];
const getRouteGlowColor = (value) => routeGlowPalette[hashString(value) % routeGlowPalette.length];

const shortenDestinationLabel = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= 22) return text;
  return `${text.slice(0, 19).trimEnd()}...`;
};

const cleanLocationPart = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const normalized = text.toLowerCase();
  if (normalized === "unknown" || normalized === "unknown city" || normalized === "unknown state") return "";
  return text;
};

const getPointLocationParts = (point) => {
  const city = [
    point?.city,
    point?.city_name,
    point?.locality,
    point?.place,
    point?.place_name,
    point?.town,
    point?.village,
    point?.district,
    point?.county,
  ]
    .map(cleanLocationPart)
    .find(Boolean);

  const state = [
    point?.state,
    point?.state_name,
    point?.region,
    point?.region_name,
    point?.province,
    point?.state_code,
    point?.region_code,
  ]
    .map(cleanLocationPart)
    .find(Boolean);

  return { city, state };
};

const formatCoordinateLocation = (point) => {
  const latitude = Number(point?.latitude);
  const longitude = Number(point?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return "-";
  return `${Math.abs(latitude).toFixed(4)}°${latitude >= 0 ? "N" : "S"}, ${Math.abs(longitude).toFixed(4)}°${longitude >= 0 ? "E" : "W"}`;
};

const getGeocodeCacheKey = (latitude, longitude) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
  return `${lat.toFixed(3)},${lng.toFixed(3)}`;
};

const extractLocationLabelFromMapboxResponse = (payload) => {
  const features = Array.isArray(payload?.features) ? payload.features : [];
  let city = "";
  let state = "";

  const readFeature = (feature) => {
    if (!feature) return;
    const placeTypes = Array.isArray(feature.place_type) ? feature.place_type : [];
    const text = cleanLocationPart(feature.text || feature.place_name);

    if (!city && ["place", "locality", "district", "neighborhood"].some((type) => placeTypes.includes(type))) {
      city = text;
    }

    if (!state && placeTypes.includes("region")) {
      state = cleanLocationPart(feature.text) || cleanLocationPart(feature.properties?.short_code?.split("-").pop()?.toUpperCase());
    }

    const context = Array.isArray(feature.context) ? feature.context : [];
    for (const item of context) {
      const itemId = String(item?.id || "");
      if (!city && ["place.", "locality.", "district.", "neighborhood."].some((prefix) => itemId.startsWith(prefix))) {
        city = cleanLocationPart(item.text);
      }
      if (!state && itemId.startsWith("region.")) {
        state = cleanLocationPart(item.text) || cleanLocationPart(item.short_code?.split("-").pop()?.toUpperCase());
      }
    }
  };

  for (const feature of features) {
    readFeature(feature);
    if (city && state) break;
  }

  if (!city && features[0]) {
    city = cleanLocationPart(features[0].text || features[0].place_name);
  }

  return [city, state].filter(Boolean).join(", ");
};

const formatPointLocation = (point, reverseGeocodeCache = {}) => {
  const explicitLabel = cleanLocationPart(point?.location_label || point?.formatted_location || point?.location);
  if (explicitLabel) return explicitLabel;

  const { city, state } = getPointLocationParts(point);
  if (city || state) return [city, state].filter(Boolean).join(", ");

  const cacheKey = getGeocodeCacheKey(point?.latitude, point?.longitude);
  if (cacheKey && reverseGeocodeCache[cacheKey]) return reverseGeocodeCache[cacheKey];

  return formatCoordinateLocation(point);
};

const getCentroid = (points) => {
  if (!points.length) return null;
  const sum = points.reduce(
    (acc, p) => ({ lat: acc.lat + Number(p.latitude), lng: acc.lng + Number(p.longitude) }),
    { lat: 0, lng: 0 },
  );
  return [sum.lng / points.length, sum.lat / points.length];
};

const isValidCoordinatePair = (latitude, longitude) => Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude));

const getBoundsFromCoordinates = (coordinates) => {
  const valid = coordinates.filter(([lng, lat]) => Number.isFinite(Number(lng)) && Number.isFinite(Number(lat)));
  if (!valid.length) return null;
  const bounds = new mapboxgl.LngLatBounds(valid[0], valid[0]);
  for (const coord of valid.slice(1)) {
    bounds.extend(coord);
  }
  return bounds;
};

const sanitizeRouteCoordinates = (coordinates) =>
  (Array.isArray(coordinates) ? coordinates : [])
    .map((pair) => [Number(pair?.[0]), Number(pair?.[1])])
    .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));

const buildTripRouteCoordinates = (trip) => {
  const routeCoordinates = sanitizeRouteCoordinates(trip?.route_geometry?.coordinates);
  const start = isValidCoordinatePair(trip?.latitude, trip?.longitude)
    ? [Number(trip.longitude), Number(trip.latitude)]
    : null;
  const destination = isValidCoordinatePair(trip?.destination_lat, trip?.destination_lng)
    ? [Number(trip.destination_lng), Number(trip.destination_lat)]
    : null;

  if (!routeCoordinates.length) {
    return start && destination ? [start, destination] : [];
  }

  const normalized = [...routeCoordinates];
  if (start) {
    const [firstLng, firstLat] = normalized[0];
    if (Math.abs(firstLng - start[0]) > 0.0001 || Math.abs(firstLat - start[1]) > 0.0001) {
      normalized.unshift(start);
    } else {
      normalized[0] = start;
    }
  }

  if (destination) {
    const [lastLng, lastLat] = normalized[normalized.length - 1];
    if (Math.abs(lastLng - destination[0]) > 0.0001 || Math.abs(lastLat - destination[1]) > 0.0001) {
      normalized.push(destination);
    } else {
      normalized[normalized.length - 1] = destination;
    }
  }

  return normalized;
};

const getTripRouteCacheKey = (trip) => {
  if (!trip?.id) return "";
  if (!isValidCoordinatePair(trip?.latitude, trip?.longitude) || !isValidCoordinatePair(trip?.destination_lat, trip?.destination_lng)) return "";
  return [
    trip.id,
    Number(trip.longitude).toFixed(4),
    Number(trip.latitude).toFixed(4),
    Number(trip.destination_lng).toFixed(4),
    Number(trip.destination_lat).toFixed(4),
  ].join(":");
};

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    summary: {},
    role_breakdown: {},
    payment_status_breakdown: {},
    users: [],
    shipments: [],
    transactions: [],
    map_points: [],
    emergency_services: [],
    emergency_reports: [],
    emergency_routes: [],
  });
  const [animatedMapPoints, setAnimatedMapPoints] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showTraffic, setShowTraffic] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [legendOffset, setLegendOffset] = useState({ x: 0, y: 0 });
  const [showEmergencyServicesLayer, setShowEmergencyServicesLayer] = useState(true);
  const [showEmergencyReportsLayer, setShowEmergencyReportsLayer] = useState(true);
  const [showEmergencyRoutesLayer, setShowEmergencyRoutesLayer] = useState(true);
  const [followLive, setFollowLive] = useState(false);
  const [mapError, setMapError] = useState("");
  const [reverseGeocodeCache, setReverseGeocodeCache] = useState({});
  const [routeGeometryCache, setRouteGeometryCache] = useState({});
  const [viewState, setViewState] = useState({ longitude: -98.5795, latitude: 39.8283, zoom: 3.5 });
  const [kycProfiles, setKycProfiles] = useState([]);
  const [kycLoading, setKycLoading] = useState(false);
  const [selectedKycUserId, setSelectedKycUserId] = useState("");
  const [selectedKycSubmission, setSelectedKycSubmission] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerZoom, setViewerZoom] = useState(1);
  const [reviewNotes, setReviewNotes] = useState("");
  const [driverTimeData, setDriverTimeData] = useState({ drivers: [], sessions: [] });
  const [emergencyActionId, setEmergencyActionId] = useState("");
  const [emergencyResponseDrafts, setEmergencyResponseDrafts] = useState({});

  const mapRef = useRef(null);
  const mapShellRef = useRef(null);
  const legendRef = useRef(null);
  const legendDragRef = useRef(null);
  const wsRef = useRef(null);
  const sourceReadyRef = useRef(false);

  const clampLegendOffset = useCallback((x, y) => {
    if (typeof window === "undefined") return { x, y };
    if (window.innerWidth >= 640) return { x: 0, y: 0 };

    const shellRect = mapShellRef.current?.getBoundingClientRect();
    const legendRect = legendRef.current?.getBoundingClientRect();
    const shellWidth = shellRect?.width || window.innerWidth;
    const shellHeight = shellRect?.height || window.innerHeight;
    const legendWidth = legendRect?.width || Math.min(shellWidth - 24, 384);
    const legendHeight = legendRect?.height || Math.min(shellHeight * 0.46, 360);
    const horizontalRoom = Math.max(0, shellWidth - legendWidth - 24);
    const verticalRoom = Math.max(0, shellHeight - legendHeight - 24);

    return {
      x: Math.min(Math.max(x, 0), horizontalRoom),
      y: Math.min(Math.max(y, -verticalRoom), 0),
    };
  }, []);

  const upsertMapPoint = (point) => {
    setData((prev) => {
      const next = [...prev.map_points];
      const idx = next.findIndex((p) => p.id === point.id);
      if (idx >= 0) {
        next[idx] = { ...next[idx], ...point };
      } else {
        next.push(point);
      }
      return { ...prev, map_points: next };
    });
  };

  const upsertEmergencyReport = (report) => {
    setData((prev) => {
      const nextReports = [...(prev.emergency_reports || [])];
      const idx = nextReports.findIndex((item) => item.id === report.id);
      if (idx >= 0) {
        nextReports[idx] = { ...nextReports[idx], ...report };
      } else {
        nextReports.unshift(report);
      }

      return {
        ...prev,
        emergency_reports: nextReports,
        summary: {
          ...prev.summary,
          emergency_open_reports: nextReports.filter((item) => item.status !== "resolved").length,
        },
      };
    });
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load admin dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchKycProfiles = useCallback(async () => {
    setKycLoading(true);
    try {
      const response = await axios.get(`${API}/admin/kyc/profiles`);
      const profiles = response.data || [];
      setKycProfiles(profiles);
      const selectedStillExists = profiles.some((p) => p.user?.id === selectedKycUserId);
      if (!selectedStillExists) {
        setSelectedKycUserId(profiles[0]?.user?.id || "");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load KYC profiles");
    } finally {
      setKycLoading(false);
    }
  }, [selectedKycUserId]);

  const fetchDriverTimeHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/driver-time/history?limit=120`);
      setDriverTimeData(response.data || { drivers: [], sessions: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load driver time history");
    }
  }, []);

  const reviewKyc = async (submissionId, status) => {
    try {
      await axios.post(`${API}/admin/kyc/${submissionId}/review`, { status, notes: reviewNotes || null });
      toast.success(`KYC marked ${status}`);
      fetchDashboard();
      fetchKycProfiles();
      setReviewNotes("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review KYC");
    }
  };

  const updateEmergencyReport = async (reportId, status) => {
    setEmergencyActionId(reportId);
    try {
      await axios.put(`${API}/admin/emergency/reports/${reportId}`, {
        status,
        response_notes: emergencyResponseDrafts[reportId] || null,
      });
      toast.success(`Emergency report ${status}`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update emergency report");
    } finally {
      setEmergencyActionId("");
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchKycProfiles();
    fetchDriverTimeHistory();
  }, [fetchDashboard, fetchDriverTimeHistory, fetchKycProfiles]);

  useEffect(() => {
    const syncLegendOffset = () => {
      if (typeof window === "undefined") return;
      if (window.innerWidth >= 640) {
        setLegendOffset({ x: 0, y: 0 });
        return;
      }
      setLegendOffset((prev) => clampLegendOffset(prev.x, prev.y));
    };

    syncLegendOffset();
    window.addEventListener("resize", syncLegendOffset);
    return () => window.removeEventListener("resize", syncLegendOffset);
  }, [clampLegendOffset, showLegend]);

  useEffect(() => {
    const token = localStorage.getItem("eagle_token");
    if (!token) return;

    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsHost = process.env.REACT_APP_BACKEND_URL
      ? process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, wsProtocol).replace(/\/$/, "")
      : `${wsProtocol}//${window.location.host}`;
    const wsUrl = `${wsHost}/api/ws/admin/live-map?token=${encodeURIComponent(token)}`;
    const socket = new WebSocket(wsUrl);
    wsRef.current = socket;

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === "location_update" && payload.point) {
          upsertMapPoint(payload.point);
          return;
        }
        if ((payload.type === "emergency_report_created" || payload.type === "emergency_report_updated") && payload.report) {
          upsertEmergencyReport(payload.report);
        }
      } catch (_) {
        // Ignore malformed frames.
      }
    };

    const pingInterval = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send("ping");
      }
    }, 25000);

    socket.onerror = () => {
      toast.error("Live map connection lost. Retrying on refresh.");
    };

    return () => {
      clearInterval(pingInterval);
      socket.close();
    };
  }, []);

  useEffect(() => {
    if (!data.map_points.length) {
      setAnimatedMapPoints([]);
      return;
    }
    let raf;
    const tick = () => {
      setAnimatedMapPoints((prev) => {
        const prevMap = new globalThis.Map(prev.map((p) => [p.id, p]));
        return data.map_points.map((target) => {
          const current = prevMap.get(target.id);
          if (!current) return target;
          const tLat = Number(target.latitude);
          const tLng = Number(target.longitude);
          const cLat = Number(current.latitude);
          const cLng = Number(current.longitude);
          const distance = Math.hypot(tLat - cLat, tLng - cLng);
          if (distance < 0.000006) return { ...target, latitude: tLat, longitude: tLng };
          return {
            ...target,
            latitude: cLat + (tLat - cLat) * 0.22,
            longitude: cLng + (tLng - cLng) * 0.22,
          };
        });
      });
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [data.map_points]);

  const renderPoints = useMemo(() => separateOverlappingPoints(animatedMapPoints), [animatedMapPoints]);
  const renderPointById = useMemo(
    () => new globalThis.Map(renderPoints.map((point) => [point.id, point])),
    [renderPoints],
  );

  const geojson = useMemo(
    () => ({
      type: "FeatureCollection",
      features: renderPoints.map((p) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [Number(p.plot_longitude ?? p.longitude), Number(p.plot_latitude ?? p.latitude)],
        },
        properties: { ...p },
      })),
    }),
    [renderPoints],
  );

  const activeTrips = useMemo(
    () =>
      (data.map_points || [])
        .filter((p) => p.point_type === "truck" && ["active", "paused"].includes(String(p.trip_status || "").toLowerCase()))
        .sort((a, b) => Number(b.route_progress_pct || 0) - Number(a.route_progress_pct || 0)),
    [data.map_points],
  );

  const destinationRenderPoints = useMemo(
    () =>
      separateOverlappingPoints(
        activeTrips
          .filter((trip) => isValidCoordinatePair(trip.destination_lat, trip.destination_lng))
          .map((trip) => ({
            ...trip,
            latitude: Number(trip.destination_lat),
            longitude: Number(trip.destination_lng),
          })),
      ),
    [activeTrips],
  );

  const destinationPointById = useMemo(
    () => new globalThis.Map(destinationRenderPoints.map((point) => [point.id, point])),
    [destinationRenderPoints],
  );
  const emergencyServiceById = useMemo(
    () => new globalThis.Map((data.emergency_services || []).map((service) => [service.id, service])),
    [data.emergency_services],
  );
  const emergencyReportById = useMemo(
    () => new globalThis.Map((data.emergency_reports || []).map((report) => [report.id, report])),
    [data.emergency_reports],
  );
  const emergencyRouteById = useMemo(
    () => new globalThis.Map((data.emergency_routes || []).map((route) => [route.id, route])),
    [data.emergency_routes],
  );

  useEffect(() => {
    if (!MAPBOX_TOKEN || activeTrips.length === 0) return;

    const tripsNeedingRouteFetch = activeTrips.filter((trip) => {
      const cacheKey = getTripRouteCacheKey(trip);
      if (!cacheKey || routeGeometryCache[cacheKey]) return false;
      const routeCoordinates = sanitizeRouteCoordinates(trip?.route_geometry?.coordinates);
      return routeCoordinates.length < 3;
    });

    if (!tripsNeedingRouteFetch.length) return;

    let cancelled = false;

    const fetchRoutes = async () => {
      const resolvedEntries = await Promise.all(
        tripsNeedingRouteFetch.map(async (trip) => {
          const cacheKey = getTripRouteCacheKey(trip);
          if (!cacheKey) return null;

          const originLng = Number(trip.longitude);
          const originLat = Number(trip.latitude);
          const destLng = Number(trip.destination_lng);
          const destLat = Number(trip.destination_lat);

          try {
            const response = await fetch(
              `https://api.mapbox.com/directions/v5/mapbox/driving-traffic/${originLng},${originLat};${destLng},${destLat}?overview=full&geometries=geojson&steps=false&access_token=${MAPBOX_TOKEN}`,
            );
            if (!response.ok) return [cacheKey, []];
            const payload = await response.json();
            const coordinates = sanitizeRouteCoordinates(payload?.routes?.[0]?.geometry?.coordinates);
            return [cacheKey, coordinates];
          } catch (_) {
            return [cacheKey, []];
          }
        }),
      );

      if (cancelled) return;

      setRouteGeometryCache((prev) => {
        const next = { ...prev };
        for (const entry of resolvedEntries) {
          if (!entry) continue;
          const [cacheKey, coordinates] = entry;
          if (!(cacheKey in next)) {
            next[cacheKey] = coordinates;
          }
        }
        return next;
      });
    };

    fetchRoutes();

    return () => {
      cancelled = true;
    };
  }, [activeTrips, routeGeometryCache]);

  const beginLegendDrag = (event) => {
    if (typeof window !== "undefined" && window.innerWidth >= 640) return;
    legendDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: legendOffset.x,
      originY: legendOffset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveLegendDrag = (event) => {
    const drag = legendDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    setLegendOffset(clampLegendOffset(drag.originX + dx, drag.originY + dy));
  };

  const endLegendDrag = (event) => {
    const drag = legendDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    legendDragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const tripRoutesGeojson = useMemo(
    () => ({
      type: "FeatureCollection",
      features: activeTrips
        .map((trip) => {
          const cacheKey = getTripRouteCacheKey(trip);
          const cachedCoordinates = cacheKey ? routeGeometryCache[cacheKey] : null;
          const coordinates = (Array.isArray(cachedCoordinates) && cachedCoordinates.length >= 3)
            ? buildTripRouteCoordinates({ ...trip, route_geometry: { type: "LineString", coordinates: cachedCoordinates } })
            : buildTripRouteCoordinates(trip);
          if (coordinates.length < 2) return null;
          return {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates,
            },
            properties: {
              id: trip.id,
              point_id: trip.id,
              entity_id: trip.entity_id,
              driver_name: trip.name,
              destination_name: trip.destination_name,
              trip_status: trip.trip_status,
              route_color: getRouteColor(trip.entity_id || trip.id),
              route_glow_color: getRouteGlowColor(trip.entity_id || trip.id),
            },
          };
        })
        .filter(Boolean),
    }),
    [activeTrips, routeGeometryCache],
  );

  const tripDestinationsGeojson = useMemo(
    () => ({
      type: "FeatureCollection",
      features: destinationRenderPoints.map((trip) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [Number(trip.plot_longitude ?? trip.longitude), Number(trip.plot_latitude ?? trip.latitude)],
        },
        properties: {
          id: `${trip.id}:destination`,
          point_id: trip.id,
          entity_id: trip.entity_id,
          driver_name: trip.name,
          destination_name: trip.destination_name,
          destination_short: shortenDestinationLabel(trip.destination_name),
          route_color: getRouteColor(trip.entity_id || trip.id),
          trip_status: trip.trip_status,
        },
      })),
    }),
    [destinationRenderPoints],
  );

  useEffect(() => {
    if (!followLive) return;

    const routeCoordinates = tripRoutesGeojson.features.flatMap((feature) =>
      Array.isArray(feature.geometry?.coordinates) ? feature.geometry.coordinates : [],
    );
    const pointCoordinates = renderPoints.map((point) => [Number(point.longitude), Number(point.latitude)]);
    const destinationCoordinates = destinationRenderPoints.map((point) => [Number(point.longitude), Number(point.latitude)]);
    const bounds = getBoundsFromCoordinates([...routeCoordinates, ...pointCoordinates, ...destinationCoordinates]);
    const map = mapRef.current?.getMap?.();

    if (bounds && map) {
      map.fitBounds(bounds, {
        padding: { top: 90, right: 110, bottom: 120, left: 90 },
        maxZoom: 9.5,
        duration: 700,
      });
      const center = bounds.getCenter();
      setViewState((prev) => ({
        ...prev,
        longitude: center.lng,
        latitude: center.lat,
        zoom: Math.min(prev.zoom, 9.5),
      }));
      return;
    }

    if (!renderPoints.length) return;
    const center = getCentroid(renderPoints);
    if (!center) return;
    setViewState((prev) => ({ ...prev, longitude: center[0], latitude: center[1] }));
  }, [destinationRenderPoints, followLive, renderPoints, tripRoutesGeojson]);

  useEffect(() => {
    if (!selectedFeature || !MAPBOX_TOKEN) return;

    const explicitLabel = cleanLocationPart(selectedFeature.location_label || selectedFeature.formatted_location || selectedFeature.location);
    const { city, state } = getPointLocationParts(selectedFeature);
    if (explicitLabel || city || state) return;

    const latitude = Number(selectedFeature.latitude);
    const longitude = Number(selectedFeature.longitude);
    const cacheKey = getGeocodeCacheKey(latitude, longitude);
    if (!cacheKey || reverseGeocodeCache[cacheKey]) return;

    const controller = new AbortController();

    const resolveLocation = async () => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?types=place,locality,district,neighborhood,region&limit=5&access_token=${MAPBOX_TOKEN}`,
          { signal: controller.signal },
        );
        if (!response.ok) throw new Error(`Geocoding failed: ${response.status}`);
        const payload = await response.json();
        const label = extractLocationLabelFromMapboxResponse(payload) || formatCoordinateLocation(selectedFeature);
        setReverseGeocodeCache((prev) => (prev[cacheKey] ? prev : { ...prev, [cacheKey]: label }));
      } catch (error) {
        if (error.name === "AbortError") return;
        setReverseGeocodeCache((prev) => (prev[cacheKey] ? prev : { ...prev, [cacheKey]: formatCoordinateLocation(selectedFeature) }));
      }
    };

    resolveLocation();
    return () => controller.abort();
  }, [reverseGeocodeCache, selectedFeature]);

  const handleMapLoad = () => {
    sourceReadyRef.current = true;
    setMapError("");
  };

  const handleMapClick = (evt) => {
    const routeFeature = evt.features?.find((f) => f.layer.id === "trip-routes-hitbox");
    if (routeFeature) {
      const pointId = routeFeature.properties?.point_id || routeFeature.properties?.id;
      const match = data.map_points.find((point) => point.id === pointId);
      const renderMatch = renderPointById.get(pointId);
      if (match) {
        setSelectedFeature({
          ...match,
          popup_longitude: Number(renderMatch?.plot_longitude ?? renderMatch?.longitude ?? match.longitude),
          popup_latitude: Number(renderMatch?.plot_latitude ?? renderMatch?.latitude ?? match.latitude),
        });
      }
      return;
    }

    const emergencyRouteFeature = evt.features?.find((f) => f.layer.id === "emergency-routes-hitbox");
    if (emergencyRouteFeature) {
      const routeId = emergencyRouteFeature.properties?.id;
      const match = emergencyRouteById.get(routeId);
      if (match) {
        setSelectedFeature({
          ...match,
          feature_kind: "emergency_route",
          popup_longitude: Number(evt.lngLat.lng),
          popup_latitude: Number(evt.lngLat.lat),
        });
      }
      return;
    }

    const destinationFeature = evt.features?.find((f) => f.layer.id === "trip-destinations");
    if (destinationFeature) {
      const pointId = destinationFeature.properties?.point_id;
      const match = data.map_points.find((point) => point.id === pointId);
      const renderDestination = destinationPointById.get(pointId);
      if (match) {
        setSelectedFeature({
          ...match,
          popup_longitude: Number(renderDestination?.plot_longitude ?? renderDestination?.longitude ?? match.longitude),
          popup_latitude: Number(renderDestination?.plot_latitude ?? renderDestination?.latitude ?? match.latitude),
        });
      }
      return;
    }

    const emergencyServiceFeature = evt.features?.find((f) => f.layer.id === "emergency-services");
    if (emergencyServiceFeature) {
      const serviceId = emergencyServiceFeature.properties?.id;
      const match = emergencyServiceById.get(serviceId);
      if (match) {
        setSelectedFeature({
          ...match,
          feature_kind: "emergency_service",
          popup_longitude: Number(emergencyServiceFeature.geometry.coordinates[0]),
          popup_latitude: Number(emergencyServiceFeature.geometry.coordinates[1]),
        });
      }
      return;
    }

    const emergencyReportFeature = evt.features?.find((f) => f.layer.id === "emergency-reports");
    if (emergencyReportFeature) {
      const reportId = emergencyReportFeature.properties?.id;
      const match = emergencyReportById.get(reportId);
      if (match) {
        setSelectedFeature({
          ...match,
          feature_kind: "emergency_report",
          popup_longitude: Number(emergencyReportFeature.geometry.coordinates[0]),
          popup_latitude: Number(emergencyReportFeature.geometry.coordinates[1]),
        });
      }
      return;
    }

    const feature = evt.features?.find((f) => f.layer.id === "fleet-unclustered");
    if (feature) {
      const coordinates = Array.isArray(feature.geometry?.coordinates) ? feature.geometry.coordinates : [];
      const popupLongitude = Number(coordinates[0]);
      const popupLatitude = Number(coordinates[1]);
      const sourceLongitude = Number(feature.properties?.longitude);
      const sourceLatitude = Number(feature.properties?.latitude);
      setSelectedFeature({
        ...feature.properties,
        longitude: Number.isFinite(sourceLongitude) ? sourceLongitude : popupLongitude,
        latitude: Number.isFinite(sourceLatitude) ? sourceLatitude : popupLatitude,
        popup_longitude: popupLongitude,
        popup_latitude: popupLatitude,
      });
      return;
    }

    const clusterFeature = evt.features?.find((f) => f.layer.id === "fleet-clusters");
    if (clusterFeature) {
      const map = mapRef.current?.getMap();
      if (!map) return;
      const source = map.getSource("fleet");
      const clusterId = clusterFeature.properties?.cluster_id;
      if (!source || clusterId === undefined) return;
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        map.easeTo({
          center: clusterFeature.geometry.coordinates,
          zoom: Math.min(zoom + 0.5, 15),
          duration: 500,
        });
      });
      return;
    }

    setSelectedFeature(null);
  };

  const recentTransactions = useMemo(() => data.transactions.slice(0, 10), [data.transactions]);
  const recentUsers = useMemo(() => data.users.slice(0, 12), [data.users]);

  const pointTypeCounts = useMemo(() => {
    const counts = { truck: 0, factory: 0, distribution_center: 0, user: 0 };
    for (const point of data.map_points) {
      const pointType = point.point_type || "user";
      if (counts[pointType] !== undefined) counts[pointType] += 1;
    }
    return counts;
  }, [data.map_points]);

  const driverStatusCounts = useMemo(() => {
    const counts = { available: 0, on_duty: 0, on_break: 0, off_duty: 0 };
    for (const point of data.map_points) {
      if (point.point_type !== "truck") continue;
      const status = point.driver_status || "off_duty";
      if (counts[status] !== undefined) counts[status] += 1;
    }
    return counts;
  }, [data.map_points]);

  const selectedKycProfile = useMemo(
    () => kycProfiles.find((p) => p.user?.id === selectedKycUserId) || null,
    [kycProfiles, selectedKycUserId],
  );
  const visibleEmergencyServices = useMemo(() => data.emergency_services || [], [data.emergency_services]);
  const emergencyReports = useMemo(() => data.emergency_reports || [], [data.emergency_reports]);
  const openEmergencyReports = useMemo(
    () => emergencyReports.filter((report) => report.status !== "resolved"),
    [emergencyReports],
  );
  const emergencyRoutes = useMemo(() => data.emergency_routes || [], [data.emergency_routes]);
  const emergencyResponders = useMemo(
    () => (data.users || []).filter((user) => user.role === "emergency"),
    [data.users],
  );
  const emergencyResponderStatusCounts = useMemo(() => {
    const counts = { available: 0, on_duty: 0, on_break: 0, off_duty: 0 };
    for (const responder of emergencyResponders) {
      const status = responder.driver_status || "off_duty";
      if (counts[status] !== undefined) counts[status] += 1;
    }
    return counts;
  }, [emergencyResponders]);
  const emergencyServiceTypeCounts = useMemo(() => {
    const counts = {};
    for (const service of visibleEmergencyServices) {
      const type = String(service.service_type || "");
      counts[type] = (counts[type] || 0) + 1;
    }
    return counts;
  }, [visibleEmergencyServices]);
  const emergencySeverityCounts = useMemo(() => {
    const counts = {};
    for (const report of openEmergencyReports) {
      const severity = String(report.severity || "");
      counts[severity] = (counts[severity] || 0) + 1;
    }
    return counts;
  }, [openEmergencyReports]);
  const emergencyServicesGeojson = useMemo(
    () => ({
      type: "FeatureCollection",
      features: visibleEmergencyServices
        .filter((service) => isValidCoordinatePair(service.latitude, service.longitude))
        .map((service) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [Number(service.longitude), Number(service.latitude)],
          },
          properties: {
            id: service.id,
            name: service.name,
            service_type: service.service_type,
            status: service.status,
            address: service.address,
            city: service.city,
            state: service.state,
            feature_kind: "emergency_service",
            marker_color: getEmergencyServiceColor(service.service_type),
          },
        })),
    }),
    [visibleEmergencyServices],
  );
  const emergencyReportsGeojson = useMemo(
    () => ({
      type: "FeatureCollection",
      features: emergencyReports
        .filter((report) => isValidCoordinatePair(report.latitude, report.longitude))
        .map((report) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [Number(report.longitude), Number(report.latitude)],
          },
          properties: {
            id: report.id,
            status: report.status,
            severity: report.severity,
            report_type: report.report_type,
            user_name: report.user_name,
            feature_kind: "emergency_report",
            marker_color: getEmergencySeverityColor(report.severity),
          },
        })),
    }),
    [emergencyReports],
  );
  const emergencyRoutesGeojson = useMemo(
    () => ({
      type: "FeatureCollection",
      features: emergencyRoutes
        .map((route) => {
          const coordinates = sanitizeRouteCoordinates(route?.route_geometry?.coordinates);
          if (coordinates.length < 2) return null;
          return {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates,
            },
            properties: {
              id: route.id,
              service_name: route.service_name,
              service_type: route.service_type,
              safe_route: route.safe_route,
              preference: route.preference,
              feature_kind: "emergency_route",
              route_color: route.safe_route ? "#38bdf8" : "#f97316",
            },
          };
        })
        .filter(Boolean),
    }),
    [emergencyRoutes],
  );
  const selectedFeatureLocationLabel = useMemo(
    () => formatPointLocation(selectedFeature, reverseGeocodeCache),
    [reverseGeocodeCache, selectedFeature],
  );
  const viewerUrl = useMemo(() => {
    if (!selectedKycSubmission?.id) return "";
    const token = localStorage.getItem("eagle_token");
    if (!token) return "";
    return `${API}/kyc/submissions/${selectedKycSubmission.id}/file-open?token=${encodeURIComponent(token)}`;
  }, [selectedKycSubmission]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0d0a08] via-[#15100c] to-[#0a0908] text-stone-100">
      <Sidebar role="admin" />
      <main className="lg:ml-64 p-4 lg:p-8 space-y-6" data-testid="admin-dashboard">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-heading font-bold tracking-tight text-orange-100">Fleet Intelligence Command</h1>
            <p className="text-stone-400 mt-1">Live dispatch telemetry, road conditions, shipments, transactions, and operator status.</p>
          </div>
          <div className="flex items-center gap-2">
            <DashboardDateTimeWidget />
            <Button
              variant="outline"
              className="border-orange-700/50 text-orange-100 hover:bg-orange-900/20"
              onClick={() => {
                fetchDashboard();
                fetchKycProfiles();
                fetchDriverTimeHistory();
              }}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-6 gap-3 lg:gap-4">
          {[
            { label: "Users", value: data.summary.total_users || 0 },
            { label: "Shipments", value: data.summary.total_shipments || 0 },
            { label: "Transactions", value: data.summary.total_transactions || 0 },
            { label: "Payment Volume", value: `$${(data.summary.total_payment_volume || 0).toLocaleString()}` },
            { label: "Active Drivers", value: data.summary.active_drivers || 0 },
            { label: "Emergency Open", value: data.summary.emergency_open_reports || 0 },
          ].map((item) => (
            <Card key={item.label} className="border border-stone-700/60 bg-stone-900/70 shadow-[0_0_20px_rgba(251,146,60,0.06)]">
              <CardContent className="p-4 lg:p-5">
                <p className="text-[11px] uppercase tracking-widest text-stone-400">{item.label}</p>
                <p className="text-2xl lg:text-3xl font-heading font-bold mt-2 text-orange-50">{item.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <Card className="xl:col-span-2 border border-stone-700/60 bg-stone-900/70 overflow-hidden shadow-[0_0_30px_rgba(251,146,60,0.08)]">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading flex items-center gap-2 text-orange-100">
                <MapPin className="w-5 h-5 text-orange-400" />
                Live U.S. Operations Map
              </CardTitle>
              <CardDescription className="text-stone-400">Real-road dispatch map with traffic, overlap-safe live tracking, active trip lines, and emergency services, incidents, and response routes.</CardDescription>
            </CardHeader>
            <CardContent>
              <div ref={mapShellRef} className="relative rounded-2xl overflow-hidden border border-stone-700/70 bg-black">
                <MapView
                  ref={mapRef}
                  mapboxAccessToken={MAPBOX_TOKEN}
                  mapLib={mapboxgl}
                  initialViewState={viewState}
                  longitude={viewState.longitude}
                  latitude={viewState.latitude}
                  zoom={viewState.zoom}
                  onMove={(evt) => setViewState(evt.viewState)}
                  mapStyle={MAP_STYLE}
                  onLoad={handleMapLoad}
                  interactiveLayerIds={["fleet-clusters", "fleet-unclustered", "trip-routes-hitbox", "trip-destinations", "emergency-services", "emergency-reports", "emergency-routes-hitbox"]}
                  onClick={handleMapClick}
                  onError={(evt) => {
                    const message = evt?.error?.message || "Map failed to load. Check Mapbox token and style access.";
                    setMapError(message);
                  }}
                  style={{ width: "100%", height: "min(68vh, 620px)" }}
                >
                  <NavigationControl position="top-left" showCompass />

                  {showTraffic && (
                    <Source id="traffic" type="vector" url="mapbox://mapbox.mapbox-traffic-v1">
                      <Layer {...trafficLayer} />
                    </Source>
                  )}

                  {tripRoutesGeojson.features.length > 0 && (
                    <Source id="trip-routes" type="geojson" data={tripRoutesGeojson}>
                      <Layer {...tripRouteGlowLayer} />
                      <Layer {...tripRouteLineLayer} />
                      <Layer {...tripRouteHitboxLayer} />
                    </Source>
                  )}

                  {tripDestinationsGeojson.features.length > 0 && (
                    <Source id="trip-destinations" type="geojson" data={tripDestinationsGeojson}>
                      <Layer {...destinationOuterLayer} />
                      <Layer {...destinationInnerLayer} />
                      <Layer {...destinationLabelLayer} />
                    </Source>
                  )}

                  {showEmergencyRoutesLayer && emergencyRoutesGeojson.features.length > 0 && (
                    <Source id="emergency-routes" type="geojson" data={emergencyRoutesGeojson}>
                      <Layer {...emergencyRouteLineLayer} />
                      <Layer {...emergencyRouteHitboxLayer} />
                    </Source>
                  )}

                  {showEmergencyServicesLayer && emergencyServicesGeojson.features.length > 0 && (
                    <Source id="emergency-services" type="geojson" data={emergencyServicesGeojson}>
                      <Layer {...emergencyServiceLayer} />
                    </Source>
                  )}

                  {showEmergencyReportsLayer && emergencyReportsGeojson.features.length > 0 && (
                    <Source id="emergency-reports" type="geojson" data={emergencyReportsGeojson}>
                      <Layer {...emergencyReportLayer} />
                    </Source>
                  )}

                  <Source id="fleet" type="geojson" data={geojson} cluster clusterMaxZoom={14} clusterRadius={60}>
                    <Layer {...clusterLayer} />
                    <Layer {...clusterCountLayer} />
                    <Layer {...pointPulseLayer} />
                    <Layer {...pointOuterLayer} id="fleet-unclustered" />
                  </Source>

                  {selectedFeature && (
                    <Popup
                      className="admin-live-map-popup"
                      longitude={selectedFeature.popup_longitude ?? selectedFeature.longitude}
                      latitude={selectedFeature.popup_latitude ?? selectedFeature.latitude}
                      closeButton
                      closeOnClick={false}
                      onClose={() => setSelectedFeature(null)}
                      anchor="bottom"
                      offset={15}
                    >
                      <div className="min-w-[250px] bg-stone-950/95 p-4 text-xs leading-relaxed text-stone-100">
                        {selectedFeature.feature_kind === "emergency_service" ? (
                          <>
                            <div className="border-b border-stone-800 pb-3">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-semibold text-sm text-orange-50">{selectedFeature.name || "Emergency Service"}</p>
                                  <p className="mt-1 text-stone-400">{getEmergencyServiceTypeLabel(selectedFeature.service_type)}</p>
                                </div>
                                <Badge className={getEmergencyServiceBadgeClass(selectedFeature.status)}>
                                  {getEmergencyServiceStatusLabel(selectedFeature.status)}
                                </Badge>
                              </div>
                            </div>
                            <div className="mt-3 space-y-2">
                              <p><span className="text-stone-500">Address:</span> {selectedFeature.address || "-"}</p>
                              <p><span className="text-stone-500">Coverage:</span> {[selectedFeature.city, selectedFeature.state].filter(Boolean).join(", ") || "Regional"}</p>
                              <p><span className="text-stone-500">Phone:</span> {selectedFeature.phone || "-"}</p>
                              <p><span className="text-stone-500">Hours:</span> {selectedFeature.hours || "-"}</p>
                              <p><span className="text-stone-500">Capabilities:</span> {Array.isArray(selectedFeature.capabilities) && selectedFeature.capabilities.length ? selectedFeature.capabilities.join(", ") : "-"}</p>
                            </div>
                          </>
                        ) : selectedFeature.feature_kind === "emergency_report" ? (
                          <>
                            <div className="border-b border-stone-800 pb-3">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                  <p className="font-semibold text-sm text-orange-50">{getEmergencyReportTypeLabel(selectedFeature.report_type)}</p>
                                  <p className="mt-1 text-stone-400">{selectedFeature.user_name || "Unknown Reporter"}</p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <Badge className={getEmergencySeverityBadgeClass(selectedFeature.severity)}>
                                    {getEmergencySeverityLabel(selectedFeature.severity)}
                                  </Badge>
                                  <Badge className={getEmergencyReportStatusBadgeClass(selectedFeature.status)}>
                                    {getEmergencyReportStatusLabel(selectedFeature.status)}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <div className="mt-3 space-y-2">
                              <p><span className="text-stone-500">Description:</span> {selectedFeature.description || "-"}</p>
                              <p><span className="text-stone-500">Reported By:</span> {selectedFeature.user_name || "-"} {selectedFeature.user_role ? `(${String(selectedFeature.user_role).replaceAll("_", " ")})` : ""}</p>
                              <p><span className="text-stone-500">Coordinates:</span> {formatCoordinateLocation(selectedFeature)}</p>
                              <p><span className="text-stone-500">Submitted:</span> {selectedFeature.created_at ? new Date(selectedFeature.created_at).toLocaleString() : "-"}</p>
                              <p><span className="text-stone-500">Response Notes:</span> {selectedFeature.response_notes || "-"}</p>
                              <p><span className="text-stone-500">Responded:</span> {selectedFeature.responded_at ? new Date(selectedFeature.responded_at).toLocaleString() : "-"}</p>
                            </div>
                          </>
                        ) : selectedFeature.feature_kind === "emergency_route" ? (
                          <>
                            <div className="border-b border-stone-800 pb-3">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-semibold text-sm text-orange-50">{selectedFeature.service_name || "Emergency Route"}</p>
                                  <p className="mt-1 text-stone-400">{getEmergencyServiceTypeLabel(selectedFeature.service_type)}</p>
                                </div>
                                <Badge className={selectedFeature.safe_route ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-100" : "border-orange-500/40 bg-orange-500/10 text-orange-100"}>
                                  {selectedFeature.safe_route ? "Safe Route" : "Priority Route"}
                                </Badge>
                              </div>
                            </div>
                            <div className="mt-3 space-y-2">
                              <p><span className="text-stone-500">Preference:</span> {String(selectedFeature.preference || "-").replaceAll("_", " ")}</p>
                              <p><span className="text-stone-500">Distance:</span> {formatEmergencyDistance(selectedFeature.distance_miles)}</p>
                              <p><span className="text-stone-500">ETA:</span> {formatEmergencyEta(selectedFeature.duration_seconds)}</p>
                              <p><span className="text-stone-500">Risk Score:</span> {Number.isFinite(Number(selectedFeature.risk_score)) ? Number(selectedFeature.risk_score).toFixed(2) : "-"}</p>
                              <p><span className="text-stone-500">Created:</span> {selectedFeature.created_at ? new Date(selectedFeature.created_at).toLocaleString() : "-"}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="border-b border-stone-800 pb-3">
                              <p className="font-semibold text-sm text-orange-50">{selectedFeature.name || "User"}</p>
                              <p className="mt-1 text-stone-400">{selectedFeature.role || "-"}</p>
                            </div>
                            <div className="mt-3 space-y-2">
                              <p><span className="text-stone-500">Status:</span> {String(selectedFeature.driver_status || "-").replaceAll("_", " ")}</p>
                              <p><span className="text-stone-500">Location:</span> {selectedFeatureLocationLabel}</p>
                              <p><span className="text-stone-500">Speed:</span> {selectedFeature.speed_mph ? `${Number(selectedFeature.speed_mph).toFixed(1)} mph` : "-"}</p>
                              <p><span className="text-stone-500">Drive Time:</span> {selectedFeature.drive_time_seconds ? formatDriveTime(selectedFeature.drive_time_seconds) : "-"}</p>
                              <p><span className="text-stone-500">Trip:</span> {selectedFeature.trip_status ? String(selectedFeature.trip_status).replaceAll("_", " ") : "-"}</p>
                              <p><span className="text-stone-500">Destination:</span> {selectedFeature.destination_name || "-"}</p>
                              <p><span className="text-stone-500">ETA:</span> {selectedFeature.eta_seconds ? formatDriveTime(selectedFeature.eta_seconds) : "-"}</p>
                              <p><span className="text-stone-500">Progress:</span> {selectedFeature.route_progress_pct ? `${Number(selectedFeature.route_progress_pct).toFixed(1)}%` : "-"}</p>
                              <p><span className="text-stone-500">Next Turn:</span> {selectedFeature.next_turn_instruction || "-"}</p>
                              <p><span className="text-stone-500">Last Updated:</span> {selectedFeature.updated_at ? new Date(selectedFeature.updated_at).toLocaleTimeString() : "-"}</p>
                            </div>
                          </>
                        )}
                      </div>
                    </Popup>
                  )}
                </MapView>

                <div className="absolute top-3 right-3 flex flex-col gap-2 z-10">
                  <Button size="sm" className="h-9 bg-stone-900/85 border border-stone-700 text-orange-100 hover:bg-stone-800" onClick={() => setShowTraffic((v) => !v)}>
                    <Radar className="w-4 h-4 mr-2" />
                    Traffic {showTraffic ? "ON" : "OFF"}
                  </Button>
                  <Button size="sm" className="h-9 bg-stone-900/85 border border-stone-700 text-orange-100 hover:bg-stone-800" onClick={() => setShowEmergencyServicesLayer((v) => !v)}>
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Services {showEmergencyServicesLayer ? "ON" : "OFF"}
                  </Button>
                  <Button size="sm" className="h-9 bg-stone-900/85 border border-stone-700 text-orange-100 hover:bg-stone-800" onClick={() => setShowEmergencyReportsLayer((v) => !v)}>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Incidents {showEmergencyReportsLayer ? "ON" : "OFF"}
                  </Button>
                  <Button size="sm" className="h-9 bg-stone-900/85 border border-stone-700 text-orange-100 hover:bg-stone-800" onClick={() => setShowEmergencyRoutesLayer((v) => !v)}>
                    <Clock className="w-4 h-4 mr-2" />
                    E-Routes {showEmergencyRoutesLayer ? "ON" : "OFF"}
                  </Button>
                  <Button size="sm" className="h-9 bg-stone-900/85 border border-stone-700 text-orange-100 hover:bg-stone-800" onClick={() => setShowLegend((v) => !v)}>
                    <Layers className="w-4 h-4 mr-2" />
                    Legend {showLegend ? "ON" : "OFF"}
                  </Button>
                  <Button
                    size="sm"
                    className="h-9 bg-stone-900/85 border border-stone-700 text-orange-100 hover:bg-stone-800"
                    onClick={() => setViewState((prev) => ({ ...prev, longitude: -98.5795, latitude: 39.8283, zoom: 3.5 }))}
                  >
                    <LocateFixed className="w-4 h-4 mr-2" />
                    Recenter
                  </Button>
                  <Button size="sm" className="h-9 bg-stone-900/85 border border-stone-700 text-orange-100 hover:bg-stone-800" onClick={() => setFollowLive((v) => !v)}>
                    Follow {followLive ? "ON" : "OFF"}
                  </Button>
                </div>

                {showLegend && (
                  <div
                    ref={legendRef}
                    className="absolute bottom-3 left-3 z-10 w-[min(92vw,24rem)] max-w-[24rem] overflow-hidden rounded-xl border border-stone-700 bg-stone-950/92 text-xs text-stone-200 shadow-[0_18px_50px_rgba(0,0,0,0.45)] backdrop-blur sm:w-auto sm:min-w-[22rem]"
                    style={{ transform: `translate3d(${legendOffset.x}px, ${legendOffset.y}px, 0)` }}
                  >
                    <div
                      className="flex items-center justify-between gap-3 border-b border-stone-700/80 px-3 py-2.5 cursor-grab active:cursor-grabbing sm:cursor-default"
                      onPointerDown={beginLegendDrag}
                      onPointerMove={moveLegendDrag}
                      onPointerUp={endLegendDrag}
                      onPointerCancel={endLegendDrag}
                      style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <GripHorizontal className="h-4 w-4 text-stone-400 sm:hidden" />
                        <div className="min-w-0">
                          <p className="font-semibold text-orange-50">Operations Legend</p>
                          <p className="text-[11px] text-stone-400 sm:hidden">Drag or scroll without losing the map</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 shrink-0 px-2 text-stone-300 hover:bg-stone-800 hover:text-orange-100"
                        onClick={() => setShowLegend(false)}
                      >
                        <XCircle className="mr-1 h-4 w-4" />
                        Close
                      </Button>
                    </div>

                    <div
                      className="max-h-[46vh] overflow-y-scroll overscroll-contain px-3 py-3 pr-2 sm:max-h-none sm:overflow-visible"
                      style={{ WebkitOverflowScrolling: "touch" }}
                    >
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <span>🚚 Trucks: {pointTypeCounts.truck}</span>
                        <span>🏭 Factories: {pointTypeCounts.factory}</span>
                        <span>🏢 Distribution: {pointTypeCounts.distribution_center}</span>
                        <span>👤 Other: {pointTypeCounts.user}</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-stone-700 grid grid-cols-2 md:grid-cols-4 gap-2">
                        <span>🟢 Available: {driverStatusCounts.available}</span>
                        <span>🔵 On Duty: {driverStatusCounts.on_duty}</span>
                        <span>🟠 On Break: {driverStatusCounts.on_break}</span>
                        <span>⚪ Off Duty: {driverStatusCounts.off_duty}</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-stone-700 grid grid-cols-2 md:grid-cols-3 gap-2">
                        <span>🛣 Active Routes: {tripRoutesGeojson.features.length}</span>
                        <span>🎯 Destinations: {tripDestinationsGeojson.features.length}</span>
                        <span>📡 Live Trips: {activeTrips.length}</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-stone-700 grid grid-cols-2 md:grid-cols-3 gap-2">
                        <span>🏥 Services: {visibleEmergencyServices.length}</span>
                        <span>🚨 Incidents: {openEmergencyReports.length}</span>
                        <span>🧭 E-Routes: {emergencyRoutesGeojson.features.length}</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-stone-700">
                        <p className="mb-2 text-[11px] uppercase tracking-wider text-stone-400">Emergency Service Colors</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {EMERGENCY_SERVICE_TYPES.map((type) => (
                            <div key={type.value} className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 rounded-full border border-white/30"
                                style={{ backgroundColor: getEmergencyServiceColor(type.value) }}
                              />
                              <span>{type.label}: {emergencyServiceTypeCounts[type.value] || 0}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-stone-700">
                        <p className="mb-2 text-[11px] uppercase tracking-wider text-stone-400">Incident Severity Colors</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {EMERGENCY_SEVERITIES.map((severity) => (
                            <div key={severity.value} className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 rounded-full border border-white/30"
                                style={{ backgroundColor: getEmergencySeverityColor(severity.value) }}
                              />
                              <span>{severity.label}: {emergencySeverityCounts[severity.value] || 0}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {mapError && (
                  <div className="absolute inset-x-4 bottom-4 z-20 rounded-lg border border-red-500/50 bg-black/90 p-3 text-xs text-red-200">
                    <p className="font-semibold">Map Load Error</p>
                    <p className="mt-1 break-words">{mapError}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-stone-700/60 bg-stone-900/70">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-orange-100">
                <Activity className="w-5 h-5 text-orange-400" />
                Role & Payment Mix
              </CardTitle>
              <CardDescription className="text-stone-400">Operational distribution by user role and transaction status.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(data.role_breakdown || {}).map(([role, count]) => (
                <div key={role} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge className={roleColors[role] || ""}>{role}</Badge>
                    <span className="font-semibold text-orange-100">{count}</span>
                  </div>
                  <div className="h-2 rounded bg-stone-800 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-600 to-amber-500" style={{ width: `${data.summary.total_users ? (count / data.summary.total_users) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}

              <div className="pt-4 border-t border-stone-700 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2 text-orange-100">
                  <CreditCard className="w-4 h-4 text-orange-400" />
                  Payment Status
                </p>
                {Object.entries(data.payment_status_breakdown || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between text-sm">
                    <span className="capitalize text-stone-400">{status.replaceAll("_", " ")}</span>
                    <span className="font-semibold text-orange-100">{count}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-stone-700 space-y-3">
                <p className="text-sm font-medium text-orange-100">Pending KYC Reviews</p>
                {(data.kyc_pending || []).length === 0 ? (
                  <p className="text-sm text-stone-400">No pending KYC submissions</p>
                ) : (
                  data.kyc_pending.slice(0, 5).map((item) => (
                    <div key={item.id} className="rounded-lg border border-stone-700 p-3 space-y-2">
                      <p className="text-xs text-stone-300">{item.driver_name || item.user_id} - {item.doc_type}</p>
                      <p className="text-[11px] text-stone-500">{item.created_at ? new Date(item.created_at).toLocaleString() : "-"}</p>
                      <div className="flex gap-2">
                        <Button size="sm" className="h-8 rounded-full px-4 bg-emerald-700 hover:bg-emerald-600 text-white" onClick={() => reviewKyc(item.id, "verified")}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                          Verify
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 rounded-full px-4 border-red-500/50 text-red-300 hover:bg-red-900/20" onClick={() => reviewKyc(item.id, "rejected")}>
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border border-stone-700/60 bg-stone-900/70">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2 text-orange-100">
              <Truck className="w-5 h-5 text-orange-400" />
              Active Trips
            </CardTitle>
            <CardDescription className="text-stone-400">Live trips from driver GPS navigation sessions.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>ETA</TableHead>
                  <TableHead>Distance</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeTrips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-stone-400">No active trips right now.</TableCell>
                  </TableRow>
                ) : (
                  activeTrips.slice(0, 20).map((trip) => (
                    <TableRow key={trip.id}>
                      <TableCell>
                        <p className="font-medium">{trip.name || trip.entity_id}</p>
                        <p className="text-xs text-stone-500">{trip.email || "-"}</p>
                      </TableCell>
                      <TableCell className="capitalize">{String(trip.trip_status || "-").replaceAll("_", " ")}</TableCell>
                      <TableCell>{trip.destination_name || "-"}</TableCell>
                      <TableCell>{trip.eta_seconds ? formatDriveTime(trip.eta_seconds) : "-"}</TableCell>
                      <TableCell>{trip.distance_remaining_miles ? `${Number(trip.distance_remaining_miles).toFixed(2)} mi` : "-"}</TableCell>
                      <TableCell>{trip.route_progress_pct ? `${Number(trip.route_progress_pct).toFixed(1)}%` : "-"}</TableCell>
                      <TableCell>{trip.updated_at ? new Date(trip.updated_at).toLocaleTimeString() : "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Tabs defaultValue="kyc" className="w-full">
          <div className="overflow-x-auto pb-1">
            <TabsList className="inline-flex min-w-max bg-stone-900 border border-stone-700">
              <TabsTrigger value="users" className="px-4">Users</TabsTrigger>
              <TabsTrigger value="transactions" className="px-4">Transactions</TabsTrigger>
              <TabsTrigger value="shipments" className="px-4">Shipments</TabsTrigger>
              <TabsTrigger value="drive-times" className="px-4">Drive Times</TabsTrigger>
              <TabsTrigger value="emergency" className="px-4">Emergency Response</TabsTrigger>
              <TabsTrigger value="kyc" className="px-4">KYC Review</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="users">
            <Card className="border border-stone-700/60 bg-stone-900/70">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-orange-100">
                  <Users className="w-5 h-5 text-orange-400" />
                  Users
                </CardTitle>
                <CardDescription className="text-stone-400">Most recent user profiles and account data.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>KYC</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-stone-400">No users found</TableCell>
                      </TableRow>
                    ) : (
                      recentUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell><Badge className={roleColors[u.role] || ""}>{u.role}</Badge></TableCell>
                          <TableCell>{u.company || "-"}</TableCell>
                          <TableCell>{u.phone || "-"}</TableCell>
                          <TableCell className="capitalize">{u.kyc_status ? String(u.kyc_status).replaceAll("_", " ") : "-"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions">
            <Card className="border border-stone-700/60 bg-stone-900/70">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-orange-100">
                  <ShieldCheck className="w-5 h-5 text-orange-400" />
                  Transactions
                </CardTitle>
                <CardDescription className="text-stone-400">Recent payment and transaction intelligence.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Payment #</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>User</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-stone-400">No transactions found</TableCell>
                      </TableRow>
                    ) : (
                      recentTransactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="font-mono text-xs">{tx.payment_number || tx.id}</TableCell>
                          <TableCell>{tx.payment_type}</TableCell>
                          <TableCell className="capitalize">{String(tx.status || "").replaceAll("_", " ")}</TableCell>
                          <TableCell>${Number(tx.amount || 0).toLocaleString()}</TableCell>
                          <TableCell className="font-mono text-xs">{tx.user_id || "-"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="shipments">
            <Card className="border border-stone-700/60 bg-stone-900/70">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-orange-100">
                  <Truck className="w-5 h-5 text-orange-400" />
                  Shipments
                </CardTitle>
                <CardDescription className="text-stone-400">Operational flow and shipment status.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tracking #</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Origin</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Driver ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.shipments.slice(0, 12).map((shipment) => (
                      <TableRow key={shipment.id}>
                        <TableCell className="font-mono text-xs">{shipment.tracking_number}</TableCell>
                        <TableCell className="capitalize">{String(shipment.status || "").replaceAll("_", " ")}</TableCell>
                        <TableCell>{shipment.origin}</TableCell>
                        <TableCell>{shipment.destination}</TableCell>
                        <TableCell className="font-mono text-xs">{shipment.driver_id || "-"}</TableCell>
                      </TableRow>
                    ))}
                    {data.shipments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-stone-400">No shipments found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="drive-times">
            <Card className="border border-stone-700/60 bg-stone-900/70">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-orange-100">
                  <Clock className="w-5 h-5 text-orange-400" />
                  Driver On-Duty Time
                </CardTitle>
                <CardDescription className="text-stone-400">Drive time runs only during On Duty, with pause/resume logs by status change.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-xl border border-stone-700 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Driver</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Total On-Duty</TableHead>
                        <TableHead>Active Session</TableHead>
                        <TableHead>Last Change</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(driverTimeData.drivers || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-stone-400">No driver time records found</TableCell>
                        </TableRow>
                      ) : (
                        (driverTimeData.drivers || []).map((driver) => (
                          <TableRow key={driver.user_id}>
                            <TableCell>
                              <p className="font-medium">{driver.name || driver.user_id}</p>
                              <p className="text-xs text-stone-500">{driver.email || "-"}</p>
                            </TableCell>
                            <TableCell className="capitalize">{String(driver.driver_status || "off_duty").replaceAll("_", " ")}</TableCell>
                            <TableCell>{formatDriveTime(driver.total_drive_time_seconds || 0)}</TableCell>
                            <TableCell>{formatDriveTime(driver.active_drive_time_seconds || 0)}</TableCell>
                            <TableCell>{driver.last_status_change_at ? new Date(driver.last_status_change_at).toLocaleString() : "-"}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                <div className="rounded-xl border border-stone-700 overflow-hidden">
                  <div className="p-3 border-b border-stone-700 text-sm text-stone-200 font-medium">Recent Completed Sessions</div>
                  <div className="max-h-[360px] overflow-y-auto">
                    {(driverTimeData.sessions || []).length === 0 ? (
                      <p className="p-4 text-sm text-stone-400">No completed sessions yet.</p>
                    ) : (
                      (driverTimeData.sessions || []).map((session) => (
                        <div key={session.id} className="p-3 border-b border-stone-800">
                          <p className="text-sm text-stone-100">{session.driver_name || session.user_id}</p>
                          <p className="text-xs text-stone-400">
                            {session.session_started_at ? new Date(session.session_started_at).toLocaleString() : "-"} -> {session.session_ended_at ? new Date(session.session_ended_at).toLocaleString() : "-"}
                          </p>
                          <p className="text-xs text-stone-400 mt-1">
                            Duration: {formatDriveTime(session.duration_seconds || 0)} | Ended On: {String(session.stopped_by_status || "-").replaceAll("_", " ")}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="emergency">
            <div className="grid grid-cols-1 xl:grid-cols-[1.35fr_0.9fr] gap-4">
              <Card className="border border-stone-700/60 bg-stone-900/70">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2 text-orange-100">
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                    Emergency Response Queue
                  </CardTitle>
                  <CardDescription className="text-stone-400">Acknowledge or resolve live emergency incidents with response notes that persist to the report record.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-lg border border-stone-700 bg-black/20 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500">Open Reports</p>
                      <p className="mt-2 text-2xl font-heading font-bold text-orange-50">{openEmergencyReports.length}</p>
                    </div>
                    <div className="rounded-lg border border-stone-700 bg-black/20 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500">Services</p>
                      <p className="mt-2 text-2xl font-heading font-bold text-orange-50">{data.emergency_services?.length || 0}</p>
                    </div>
                    <div className="rounded-lg border border-stone-700 bg-black/20 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500">Saved Routes</p>
                      <p className="mt-2 text-2xl font-heading font-bold text-orange-50">{emergencyRoutes.length}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="rounded-lg border border-stone-700 bg-black/20 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500">Responders</p>
                      <p className="mt-2 text-2xl font-heading font-bold text-orange-50">{emergencyResponders.length}</p>
                    </div>
                    <div className="rounded-lg border border-stone-700 bg-black/20 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500">On Duty</p>
                      <p className="mt-2 text-2xl font-heading font-bold text-orange-50">{emergencyResponderStatusCounts.on_duty}</p>
                    </div>
                    <div className="rounded-lg border border-stone-700 bg-black/20 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500">Available</p>
                      <p className="mt-2 text-2xl font-heading font-bold text-orange-50">{emergencyResponderStatusCounts.available}</p>
                    </div>
                    <div className="rounded-lg border border-stone-700 bg-black/20 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500">On Break</p>
                      <p className="mt-2 text-2xl font-heading font-bold text-orange-50">{emergencyResponderStatusCounts.on_break}</p>
                    </div>
                    <div className="rounded-lg border border-stone-700 bg-black/20 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500">Off Duty</p>
                      <p className="mt-2 text-2xl font-heading font-bold text-orange-50">{emergencyResponderStatusCounts.off_duty}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {emergencyReports.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-stone-700 p-4 text-sm text-stone-400">No emergency reports have been submitted yet.</p>
                    ) : (
                      emergencyReports.slice(0, 12).map((report) => (
                        <div key={report.id} className="rounded-xl border border-stone-700 bg-black/20 p-4 space-y-3">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="font-medium text-stone-100">{getEmergencyReportTypeLabel(report.report_type)}</p>
                              <p className="text-xs text-stone-400">{report.user_name || report.user_id} · {report.created_at ? new Date(report.created_at).toLocaleString() : "-"}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Badge className={getEmergencySeverityBadgeClass(report.severity)}>{getEmergencySeverityLabel(report.severity)}</Badge>
                              <Badge className={getEmergencyReportStatusBadgeClass(report.status)}>{getEmergencyReportStatusLabel(report.status)}</Badge>
                            </div>
                          </div>
                          <p className="text-sm text-stone-300">{report.description}</p>
                          <p className="text-xs text-stone-500">
                            {Number(report.latitude).toFixed(5)}, {Number(report.longitude).toFixed(5)}
                          </p>
                          <Textarea
                            value={emergencyResponseDrafts[report.id] ?? report.response_notes ?? ""}
                            onChange={(event) => setEmergencyResponseDrafts((prev) => ({ ...prev, [report.id]: event.target.value }))}
                            placeholder="Response notes, dispatch instructions, or closure summary"
                            className="min-h-[88px] border-stone-700 bg-stone-950/70"
                          />
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-500"
                              disabled={emergencyActionId === report.id || report.status === "acknowledged"}
                              onClick={() => updateEmergencyReport(report.id, "acknowledged")}
                            >
                              {emergencyActionId === report.id ? "Saving..." : "Acknowledge"}
                            </Button>
                            <Button
                              size="sm"
                              className="bg-emerald-700 hover:bg-emerald-600 text-white"
                              disabled={emergencyActionId === report.id || report.status === "resolved"}
                              onClick={() => updateEmergencyReport(report.id, "resolved")}
                            >
                              {emergencyActionId === report.id ? "Saving..." : "Resolve"}
                            </Button>
                            {report.responded_at && (
                              <span className="self-center text-xs text-stone-500">
                                Last response {new Date(report.responded_at).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-stone-700/60 bg-stone-900/70">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2 text-orange-100">
                    <MapPin className="w-5 h-5 text-orange-400" />
                    Emergency Routing Activity
                  </CardTitle>
                  <CardDescription className="text-stone-400">Recent emergency routes and currently registered service coverage.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-orange-100">Recent Routes</p>
                    {emergencyRoutes.length === 0 ? (
                      <p className="text-sm text-stone-400">No emergency routes saved yet.</p>
                    ) : (
                      emergencyRoutes.slice(0, 6).map((route) => (
                        <div key={route.id} className="rounded-lg border border-stone-700 bg-black/20 p-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium text-stone-100">{route.service_name}</p>
                            <Badge variant="outline" className="border-stone-600 text-stone-200">{getEmergencyServiceTypeLabel(route.service_type)}</Badge>
                          </div>
                          <p className="mt-1 text-xs text-stone-400">
                            {formatEmergencyDistance(route.distance_miles)} · {formatEmergencyEta(route.duration_seconds)} · Risk {Number(route.risk_score || 0).toFixed(2)}
                          </p>
                          <p className="mt-1 text-xs text-stone-500">
                            {route.created_at ? new Date(route.created_at).toLocaleString() : "-"}
                          </p>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-orange-100">Service Readiness</p>
                    {(data.emergency_services || []).slice(0, 8).map((service) => (
                      <div key={service.id} className="flex items-center justify-between gap-3 rounded-lg border border-stone-700 bg-black/20 px-3 py-2">
                        <div>
                          <p className="text-sm text-stone-100">{service.name}</p>
                          <p className="text-xs text-stone-500">{getEmergencyServiceTypeLabel(service.service_type)}</p>
                        </div>
                        <Badge variant="outline" className="border-stone-600 text-stone-200">
                          {getEmergencyServiceStatusLabel(service.status)}
                        </Badge>
                      </div>
                    ))}
                    {(data.emergency_services || []).length === 0 && (
                      <p className="text-sm text-stone-400">No emergency services loaded.</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-orange-100">Responder Tracking</p>
                    {emergencyResponders.slice(0, 8).map((responder) => (
                      <div key={responder.id} className="rounded-lg border border-stone-700 bg-black/20 px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm text-stone-100">{responder.name}</p>
                            <p className="text-xs text-stone-500">{responder.email}</p>
                          </div>
                          <Badge variant="outline" className="border-stone-600 text-stone-200">
                            {String(responder.driver_status || "off_duty").replaceAll("_", " ")}
                          </Badge>
                        </div>
                        <p className="mt-2 text-xs text-stone-500">
                          {responder.current_location?.location_label || responder.current_location?.city || "Location pending"}
                        </p>
                        <p className="mt-1 text-xs text-stone-600">
                          {responder.current_location?.tracked_at ? `Last seen ${new Date(responder.current_location.tracked_at).toLocaleString()}` : "Awaiting telemetry"}
                        </p>
                      </div>
                    ))}
                    {emergencyResponders.length === 0 && (
                      <p className="text-sm text-stone-400">No emergency responders registered yet.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="kyc">
            <Card className="border border-stone-700/60 bg-stone-900/70">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-orange-100">
                  <ShieldCheck className="w-5 h-5 text-orange-400" />
                  KYC Review Center
                </CardTitle>
                <CardDescription className="text-stone-400">Open full KYC profiles, review documents, and complete compliance actions.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-4">
                  <div className="rounded-xl border border-stone-700 overflow-hidden">
                    <div className="p-3 border-b border-stone-700 flex items-center justify-between">
                      <p className="text-sm text-stone-200 font-medium">Driver Profiles</p>
                      <Button size="sm" variant="outline" className="h-8" onClick={fetchKycProfiles} disabled={kycLoading}>
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${kycLoading ? "animate-spin" : ""}`} />
                        Refresh
                      </Button>
                    </div>
                    <div className="max-h-[520px] overflow-y-auto">
                      {kycProfiles.length === 0 ? (
                        <p className="p-4 text-sm text-stone-400">No KYC submissions yet</p>
                      ) : (
                        kycProfiles.map((profile) => (
                          <button
                            key={profile.user?.id || Math.random()}
                            className={`w-full text-left p-3 border-b border-stone-800 hover:bg-stone-800/50 ${selectedKycUserId === profile.user?.id ? "bg-stone-800/70" : ""}`}
                            onClick={() => setSelectedKycUserId(profile.user?.id || "")}
                          >
                            <p className="text-sm text-stone-100 font-medium">{profile.user?.name || profile.user?.id}</p>
                            <p className="text-xs text-stone-400">{profile.user?.email}</p>
                            <div className="mt-2 flex items-center gap-2">
                              <Badge variant="outline" className="capitalize">{String(profile.user?.kyc_status || "not_submitted").replaceAll("_", " ")}</Badge>
                              <Badge variant="outline">{profile.submissions?.length || 0} docs</Badge>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-stone-700 p-4">
                    {!selectedKycProfile ? (
                      <p className="text-stone-400">Select a profile to review documents.</p>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="rounded-lg border border-stone-700 p-3 text-sm">
                            <p className="text-stone-100 font-medium">{selectedKycProfile.user?.name}</p>
                            <p className="text-stone-400">{selectedKycProfile.user?.email}</p>
                            <p className="text-stone-400">{selectedKycProfile.user?.phone || "-"}</p>
                            <p className="text-stone-400 capitalize mt-1">Role: {selectedKycProfile.user?.role}</p>
                            <p className="text-stone-400 capitalize">Status: {String(selectedKycProfile.user?.kyc_status || "not_submitted").replaceAll("_", " ")}</p>
                          </div>
                          <div className="rounded-lg border border-stone-700 p-3 text-sm">
                            <p className="text-stone-200 font-medium mb-2">Review Notes</p>
                            <Textarea
                              placeholder="Add review note, rejection reason, or request details..."
                              value={reviewNotes}
                              onChange={(e) => setReviewNotes(e.target.value)}
                              className="min-h-20 bg-stone-950/60 border-stone-700"
                            />
                          </div>
                        </div>

                        <div className="rounded-lg border border-stone-700 overflow-hidden">
                          <div className="p-3 border-b border-stone-700 text-sm text-stone-200 font-medium">Submitted Documents</div>
                          <div className="max-h-[420px] overflow-y-auto">
                            {(selectedKycProfile.submissions || []).length === 0 ? (
                              <p className="p-3 text-sm text-stone-400">No documents submitted.</p>
                            ) : (
                              selectedKycProfile.submissions.map((doc) => (
                                <div key={doc.id} className="p-3 border-b border-stone-800 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                  <div>
                                    <p className="text-sm text-stone-100 capitalize">{String(doc.doc_type || "").replaceAll("_", " ")}</p>
                                    <p className="text-xs text-stone-400">{doc.file_name} · {doc.created_at ? new Date(doc.created_at).toLocaleString() : "-"}</p>
                                    <p className="text-xs text-stone-500 capitalize">Status: {String(doc.status || "pending").replaceAll("_", " ")}</p>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => { setSelectedKycSubmission(doc); setViewerOpen(true); setViewerZoom(1); }}>
                                      View
                                    </Button>
                                    <Button size="sm" className="h-8 rounded-full px-4 bg-emerald-700 hover:bg-emerald-600 text-white" onClick={() => reviewKyc(doc.id, "verified")}>Approve</Button>
                                    <Button size="sm" variant="outline" className="h-8 rounded-full px-4 border-amber-500/50 text-amber-300 hover:bg-amber-900/20" onClick={() => reviewKyc(doc.id, "needs_update")}>Needs Update</Button>
                                    <Button size="sm" variant="outline" className="h-8 rounded-full px-4 border-red-500/50 text-red-300 hover:bg-red-900/20" onClick={() => reviewKyc(doc.id, "rejected")}>Reject</Button>
                                    <Button size="sm" variant="outline" className="h-8 rounded-full px-4 border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-900/20" onClick={() => reviewKyc(doc.id, "flagged")}>Flag</Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
          <DialogContent className="max-w-5xl bg-stone-950 border-stone-700">
            <DialogHeader>
              <DialogTitle className="text-stone-100">KYC Document Viewer</DialogTitle>
            </DialogHeader>
            {selectedKycSubmission && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-stone-400">{selectedKycSubmission.file_name}</p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setViewerZoom((z) => Math.max(0.5, z - 0.1))}>-</Button>
                    <span className="text-xs text-stone-300">{Math.round(viewerZoom * 100)}%</span>
                    <Button size="sm" variant="outline" onClick={() => setViewerZoom((z) => Math.min(2.5, z + 0.1))}>+</Button>
                    <Button size="sm" variant="outline" onClick={() => viewerUrl && window.open(viewerUrl, "_blank")}>Fullscreen</Button>
                  </div>
                </div>
                <div className="h-[70vh] overflow-auto rounded-md border border-stone-700 bg-black/60 p-2">
                  {(selectedKycSubmission.mime_type || "").includes("pdf") ? (
                    <iframe title="kyc-pdf" src={viewerUrl} className="w-full h-full rounded" />
                  ) : (
                    <img
                      src={viewerUrl}
                      alt={selectedKycSubmission.file_name}
                      className="mx-auto"
                      style={{ transform: `scale(${viewerZoom})`, transformOrigin: "top center" }}
                    />
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <SiteFooter className="mt-6" />
      </main>
    </div>
  );
};

export default AdminDashboard;
